import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import {
  Bold,
  Calendar,
  Check,
  CheckSquare,
  Circle,
  Columns3,
  Diamond,
  Edit2,
  ExternalLink,
  Grid3X3,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Menu,
  MoreHorizontal,
  Paperclip,
  Search,
  Send,
  Settings,
  Strikethrough,
  Tag,
  Trash2,
  Underline,
  X,
} from 'lucide-react'
import { issuesApi } from '../api/issues'
import { commentsApi } from '../api/comments'
import { attachmentsApi, getAttachmentFileUrl } from '../api/attachments'
import { projectsApi } from '../api/projects'
import { timeLogsApi } from '../api/timeLogs'
import { useAuthStore } from '../store/authStore'
import { Avatar } from '../components/common/Avatar'
import { Button } from '../components/common/Button'
import { Modal } from '../components/common/Modal'
import IssueForm, { type CreateIssuePayload } from '../components/issues/IssueForm'
import ReadyForNextStageModal from '../components/issues/ReadyForNextStageModal'
import { copyTextToClipboard } from '../lib/clipboard'
import { prependIssueToSingleCache, syncIssueAcrossProjectCaches } from '../lib/issueCache'
import {
  createInlineMediaDraft,
  descriptionToEditableHtml,
  isInlineMediaFile,
  IssueDescriptionContent,
  stripPendingInlineMedia,
  type InlineMediaDraft,
} from '../lib/issueDescription'
import { finalizeInlineMediaForExistingIssue, finalizeIssueAttachmentsAndInlineMedia } from '../lib/issueSubmission'
import { invalidateIssueViews, invalidateWorkloadViews } from '../lib/queryInvalidation'
import { isDoneStatus, isReadyForNextStageStatus, requiresStatusDetailsPopup } from '../lib/readyForNextStage'
import { submitStatusTransitionWithInternalNote } from '../lib/statusTransitionSubmission'
import { WORK_TYPE_OPTIONS, formatWorkType } from '../lib/workTypes'
import {
  ALLOWED_ATTACHMENT_ACCEPT,
  getAllowedAttachmentMessage,
  isAllowedAttachmentFile,
} from '../lib/attachments'
import {
  canCreateSubIssue as canCreateSubIssueForRole,
  canDeleteIssue as canDeleteIssueForRole,
  canEditIssue as canEditIssueForRole,
  canMoveIssue,
  hasFullAccess,
  isTesterRole,
} from '../lib/roles'
import type { ActivityLog, Issue, TimeLog, TimeLogStatus, User, WorkType } from '../types'

const priorityOptions = [
  { value: 'NO_PRIORITY', label: 'No priority' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
] as const

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const EMPTY_EDITOR_HTML = '<p><br></p>'

function normalizeRichTextEditorValue(value: string) {
  const trimmed = value.trim()
  return trimmed === EMPTY_EDITOR_HTML ? '' : trimmed
}

function hasRichTextContent(value: string) {
  const normalized = normalizeRichTextEditorValue(value)
  if (!normalized) return false

  const hasInlineImage = /<img[\s\S]*?>/i.test(normalized) || /!\[[^\]]*\]\(([^)]+)\)/.test(normalized)
  const plainText = normalized
    .replace(/<img[\s\S]*?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim()

  return hasInlineImage || plainText.length > 0
}
const toTimeLogHours = (hours: string, minutes: string) =>
  (parseInt(hours || '0', 10) || 0) + ((parseInt(minutes || '0', 10) || 0) / 60)

const splitTimeLogHours = (hours: number) => {
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  return {
    hours: String(wholeHours),
    minutes: String(minutes),
  }
}

const formatLoggedHours = (hours: number) =>
  Number.isInteger(hours) ? `${hours}h` : `${Number(hours.toFixed(2))}h`

type TimeLogWorkTypeValue = WorkType | ''
type TimeLogStatusValue = TimeLogStatus | ''

const formatTimeLogStatus = (status: TimeLogStatus) =>
  status === 'COMPLETED' ? 'Completed' : 'Progressing'

const getTimeLogSummary = (entry: TimeLog) =>
  entry.status === 'COMPLETED'
    ? entry.completedDetails || entry.notes || ''
    : entry.progressNotes || entry.notes || ''

const getTimeLogHoverText = (entry: TimeLog) => {
  const lines = [
    `Status: ${formatTimeLogStatus(entry.status)}`,
    `Work Type: ${formatWorkType(entry.workType)}`,
    `Hours: ${formatLoggedHours(entry.hours)}`,
  ]

  if (entry.status === 'PROGRESSING' && typeof entry.progressPercent === 'number') {
    lines.push(`Progress: ${entry.progressPercent}%`)
  }

  const summary = getTimeLogSummary(entry)
  if (summary) {
    lines.push('')
    lines.push(summary)
  }

  return lines.join('\n')
}

function buildTimeLogInternalNote(options: {
  issueTitle: string
  mode: 'created' | 'updated'
  workType: WorkType
  status: TimeLogStatus
  hours: number
  loggedDate: string
  progressPercent?: number
  progressNotes?: string | null
  completedDetails?: string | null
}) {
  const details = options.status === 'COMPLETED'
    ? options.completedDetails?.trim() || ''
    : options.progressNotes?.trim() || ''
  return details
}

function formatDateValue(value: unknown): string | null {
  if (!value) return null
  const text = typeof value === 'string' ? value : String(value)
  const date = new Date(text)
  if (Number.isNaN(date.getTime())) return text
  return format(date, 'dd MMM yyyy, h:mm a')
}

function formatActivityMessage(
  entry: ActivityLog,
  helpers: {
    members: User[]
    labels: Array<{ id: string; name: string }>
    statuses: Array<{ id: string; name: string }>
  }
): string {
  const oldValue = entry.oldValue as Record<string, unknown> | string | number | null | undefined
  const newValue = entry.newValue as Record<string, unknown> | string | number | null | undefined

  const resolveUserName = (value: unknown) => {
    const id = typeof value === 'string' ? value : typeof value === 'object' && value && 'userId' in value ? value.userId : null
    if (!id || typeof id !== 'string') return null
    return helpers.members.find((member) => member.id === id)?.fullName || id
  }

  const resolveLabelName = (value: unknown) => {
    const id = typeof value === 'string' ? value : null
    if (!id) return null
    return helpers.labels.find((label) => label.id === id)?.name || id
  }

  const resolveStatusName = (value: unknown) => {
    const id = typeof value === 'string' ? value : null
    if (!id) return null
    return helpers.statuses.find((status) => status.id === id)?.name || id
  }

  switch (entry.actionType) {
    case 'ISSUE_CREATED':
      return 'created this card.'
    case 'TITLE_CHANGED':
      return `changed the title to "${String(newValue || '')}".`
    case 'DESCRIPTION_CHANGED':
      return 'updated the description.'
    case 'PRIORITY_CHANGED':
      return `changed the priority from "${String(oldValue || 'None')}" to "${String(newValue || 'None')}".`
    case 'MILESTONE_CHANGED':
      return 'updated the milestone.'
    case 'COMPONENT_CHANGED':
      return 'updated the component.'
    case 'ESTIMATE_CHANGED':
      return `changed the total estimate to ${String(newValue ?? '0')} hour(s).`
    case 'WORK_ESTIMATE_BREAKDOWN_CHANGED':
      return 'updated the work estimate breakdown.'
    case 'START_DATE_CHANGED':
      return `changed the start date to ${formatDateValue(newValue) || 'none'}.`
    case 'DUE_DATE_CHANGED':
      return `changed the due date to ${formatDateValue(newValue) || 'none'}.`
    case 'STATUS_CHANGED': {
      const fromLabel = resolveStatusName(oldValue) || String(oldValue || 'Unknown')
      const toLabel = resolveStatusName(newValue) || String(newValue || 'Unknown')
      return `moved this card from ${fromLabel} to ${toLabel}.`
    }
    case 'ASSIGNEE_ADDED':
      return `assigned this card to ${resolveUserName(newValue) || 'someone'}.`
    case 'ASSIGNEE_REMOVED':
      return `removed ${resolveUserName(oldValue) || 'someone'} from this card.`
    case 'ASSIGNEE_ESTIMATE_CHANGED': {
      const payload = (newValue && typeof newValue === 'object' ? newValue : {}) as { userId?: string; estimatedHours?: number | null }
      return `updated ${resolveUserName(payload.userId) || 'the assignee'} estimate to ${payload.estimatedHours ?? 0} hour(s).`
    }
    case 'LABEL_ADDED':
      return `added the label "${resolveLabelName(newValue) || 'Unknown'}".`
    case 'LABEL_REMOVED':
      return `removed the label "${resolveLabelName(oldValue) || 'Unknown'}".`
    case 'COMMENT_DELETED': {
      const deletedComment = oldValue && typeof oldValue === 'object' ? oldValue as { isInternal?: boolean } : {}
      return deletedComment.isInternal ? 'deleted an internal note.' : 'deleted a comment.'
    }
    case 'ISSUE_DELETED':
      return 'deleted this card.'
    default:
      return entry.actionType.toLowerCase().replace(/_/g, ' ')
  }
}

export default function IssuePage() {
  const { issueId } = useParams<{ issueId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const commentInlineImageInputRef = useRef<HTMLInputElement | null>(null)
  const commentEditorRef = useRef<HTMLDivElement | null>(null)
  const assigneePickerRef = useRef<HTMLDivElement | null>(null)
  const attachmentSectionRef = useRef<HTMLDivElement | null>(null)

  const [comment, setComment] = useState('')
  const [commentInlineMedia, setCommentInlineMedia] = useState<InlineMediaDraft[]>([])
  const [isInternal, setIsInternal] = useState(true)
  const [logOpen, setLogOpen] = useState(false)
  const [logUserId, setLogUserId] = useState(user?.id || '')
  const [logDate, setLogDate] = useState(todayDateInput())
  const [logWorkType, setLogWorkType] = useState<TimeLogWorkTypeValue>('')
  const [logStatus, setLogStatus] = useState<TimeLogStatusValue>('')
  const [logHoursValue, setLogHoursValue] = useState('0')
  const [logMinutesValue, setLogMinutesValue] = useState('0')
  const [logProgressPercent, setLogProgressPercent] = useState('')
  const [logProgressNotes, setLogProgressNotes] = useState('')
  const [logCompletedDetails, setLogCompletedDetails] = useState('')
  const [editingTimeLog, setEditingTimeLog] = useState<TimeLog | null>(null)
  const [changeStatusOpen, setChangeStatusOpen] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false)
  const [assigneeSearch, setAssigneeSearch] = useState('')
  const [subIssueOpen, setSubIssueOpen] = useState(false)
  const [editIssueOpen, setEditIssueOpen] = useState(false)
  const [copyLinkState, setCopyLinkState] = useState<'idle' | 'copied'>('idle')
  const canPostInternalComment = user?.role !== 'CLIENT'

  const { data: issueData, isLoading } = useQuery({
    queryKey: ['issue', issueId],
    queryFn: () => issuesApi.getIssue(issueId!),
    enabled: !!issueId,
  })

  const issue = issueData?.data?.data

  const { data: commentsData } = useQuery({
    queryKey: ['comments', issueId],
    queryFn: () => commentsApi.listComments(issueId!),
    enabled: !!issueId,
  })

  const { data: activityData } = useQuery({
    queryKey: ['activity', issueId],
    queryFn: () => issuesApi.listActivity(issueId!),
    enabled: !!issueId,
  })

  const { data: attachmentsData } = useQuery({
    queryKey: ['attachments', issueId],
    queryFn: () => attachmentsApi.listAttachments(issueId!),
    enabled: !!issueId,
  })

  const { data: timeLogsData } = useQuery({
    queryKey: ['time-logs', issueId],
    queryFn: () => timeLogsApi.listTimeLogs(issueId!),
    enabled: !!issueId,
  })

  const { data: statusesData } = useQuery({
    queryKey: ['statuses', issue?.projectId],
    queryFn: () => projectsApi.listStatuses(issue!.projectId),
    enabled: !!issue?.projectId,
  })

  const { data: membersData } = useQuery({
    queryKey: ['members', issue?.projectId],
    queryFn: () => projectsApi.listMembers(issue!.projectId),
    enabled: !!issue?.projectId,
  })

  const { data: labelsData } = useQuery({
    queryKey: ['labels', issue?.projectId],
    queryFn: () => projectsApi.listLabels(issue!.projectId),
    enabled: !!issue?.projectId,
  })

  const { data: subIssuesData } = useQuery({
    queryKey: ['sub-issues', issueId],
    queryFn: () => issuesApi.listSubIssues(issueId!),
    enabled: !!issueId,
  })

  useEffect(() => {
    if (!assigneePickerOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!assigneePickerRef.current?.contains(event.target as Node)) {
        setAssigneePickerOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAssigneePickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [assigneePickerOpen])

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Issue> & Record<string, unknown>) => issuesApi.updateIssue(issueId!, data),
    onSuccess: async (response) => {
      const updatedIssue = response.data.data
      qc.setQueryData(['issue', issueId], response)
      syncIssueAcrossProjectCaches(qc, updatedIssue.projectId, updatedIssue)
      await Promise.all([
        invalidateIssueViews(qc, updatedIssue.projectId),
        qc.invalidateQueries({ queryKey: ['issue', issueId] }),
        qc.invalidateQueries({ queryKey: ['time-logs', issueId] }),
      ])
    },
  })

  const createLabelMutation = useMutation({
    mutationFn: (name: string) => projectsApi.createLabel(issue!.projectId, { name, color: '#ec4899' }),
    onSuccess: (response) => {
      const createdLabel = response.data.data
      const currentLabelIds = issue?.labels.map(({ label }) => label.id) || []
      updateMutation.mutate({ labelIds: [...currentLabelIds, createdLabel.id] })
      setNewLabelName('')
      qc.invalidateQueries({ queryKey: ['labels', issue?.projectId] })
    },
  })

  const commentMutation = useMutation({
    mutationFn: async (content: string) => {
      const finalContent = await finalizeInlineMediaForExistingIssue({
        issueId: issueId!,
        content,
        inlineMedia: commentInlineMedia,
      })

      return commentsApi.createComment(issueId!, {
        content: finalContent || '',
        isInternal: canPostInternalComment ? isInternal : false,
      })
    },
    onSuccess: () => {
      setComment('')
      setCommentInlineMedia([])
      setIsInternal(true)
      if (commentEditorRef.current) {
        commentEditorRef.current.innerHTML = ''
      }
      qc.invalidateQueries({ queryKey: ['comments', issueId] })
      qc.invalidateQueries({ queryKey: ['activity', issueId] })
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['attachments', issueId] })
    },
  })

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => commentsApi.deleteComment(issueId!, commentId),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['comments', issueId] }),
        qc.invalidateQueries({ queryKey: ['activity', issueId] }),
      ])
    },
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) => attachmentsApi.uploadAttachment(issueId!, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', issueId] }),
  })

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => attachmentsApi.deleteAttachment(issueId!, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', issueId] }),
  })

  const timeLogMutation = useMutation({
    mutationFn: () => timeLogsApi.createTimeLog(issueId!, {
      userId: logUserId || user?.id,
      workType: logWorkType as WorkType,
      status: logStatus as TimeLogStatus,
      hours: toTimeLogHours(logHoursValue, logMinutesValue),
      progressPercent: logStatus === 'PROGRESSING' ? parseInt(logProgressPercent, 10) : undefined,
      progressNotes: logStatus === 'PROGRESSING' ? logProgressNotes.trim() : undefined,
      completedDetails: logStatus === 'COMPLETED' ? logCompletedDetails.trim() : undefined,
      loggedDate: `${logDate}T00:00:00.000Z`,
      internalNote: {
        content: buildTimeLogInternalNote({
          issueTitle: issue!.title,
          mode: 'created',
          workType: logWorkType as WorkType,
          status: logStatus as TimeLogStatus,
          hours: toTimeLogHours(logHoursValue, logMinutesValue),
          loggedDate: `${logDate}T00:00:00.000Z`,
          progressPercent: logStatus === 'PROGRESSING' ? parseInt(logProgressPercent, 10) : undefined,
          progressNotes: logStatus === 'PROGRESSING' ? logProgressNotes.trim() : undefined,
          completedDetails: logStatus === 'COMPLETED' ? logCompletedDetails.trim() : undefined,
        }),
      },
    }),
    onSuccess: () => {
      resetTimeModal()
      qc.invalidateQueries({ queryKey: ['time-logs', issueId] })
      qc.invalidateQueries({ queryKey: ['comments', issueId] })
      invalidateWorkloadViews(qc)
    },
  })

  const updateTimeLogMutation = useMutation({
    mutationFn: () => timeLogsApi.updateTimeLog(issueId!, editingTimeLog!.id, {
      workType: logWorkType as WorkType,
      status: logStatus as TimeLogStatus,
      hours: toTimeLogHours(logHoursValue, logMinutesValue),
      progressPercent: logStatus === 'PROGRESSING' ? parseInt(logProgressPercent, 10) : null,
      progressNotes: logStatus === 'PROGRESSING' ? logProgressNotes.trim() : null,
      completedDetails: logStatus === 'COMPLETED' ? logCompletedDetails.trim() : null,
      loggedDate: `${logDate}T00:00:00.000Z`,
      internalNote: {
        content: buildTimeLogInternalNote({
          issueTitle: issue!.title,
          mode: 'updated',
          workType: logWorkType as WorkType,
          status: logStatus as TimeLogStatus,
          hours: toTimeLogHours(logHoursValue, logMinutesValue),
          loggedDate: `${logDate}T00:00:00.000Z`,
          progressPercent: logStatus === 'PROGRESSING' ? parseInt(logProgressPercent, 10) : undefined,
          progressNotes: logStatus === 'PROGRESSING' ? logProgressNotes.trim() : undefined,
          completedDetails: logStatus === 'COMPLETED' ? logCompletedDetails.trim() : undefined,
        }),
      },
    }),
    onSuccess: () => {
      resetTimeModal()
      qc.invalidateQueries({ queryKey: ['time-logs', issueId] })
      qc.invalidateQueries({ queryKey: ['comments', issueId] })
      invalidateWorkloadViews(qc)
    },
  })

  const deleteTimeLogMutation = useMutation({
    mutationFn: (timeLogId: string) => timeLogsApi.deleteTimeLog(issueId!, timeLogId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['time-logs', issueId] })
      invalidateWorkloadViews(qc)
    },
  })

  const createSubIssueMutation = useMutation({
    mutationFn: async ({ attachments: newAttachments, inlineMedia, ...data }: CreateIssuePayload) => {
      const created = await issuesApi.createSubIssue(issueId!, {
        ...data,
        description: stripPendingInlineMedia(data.description),
      })
      const subIssueId = created.data.data.id
      const finalDescription = await finalizeIssueAttachmentsAndInlineMedia({
        issueId: subIssueId,
        description: data.description,
        attachments: newAttachments,
        inlineMedia,
      })
      if (finalDescription !== data.description) {
        return issuesApi.updateIssue(subIssueId, { description: finalDescription })
      }
      return created
    },
    onSuccess: async (response) => {
      prependIssueToSingleCache(qc, ['sub-issues', issueId], response.data.data)
      setSubIssueOpen(false)
      await Promise.all([
        invalidateIssueViews(qc, issue?.projectId),
        qc.invalidateQueries({ queryKey: ['sub-issues', issueId] }),
        qc.invalidateQueries({ queryKey: ['issue', issueId] }),
        qc.invalidateQueries({ queryKey: ['activity', issueId] }),
      ])
    },
  })

  const editIssueMutation = useMutation({
    mutationFn: async ({ attachments: newAttachments, inlineMedia, ...data }: CreateIssuePayload) => {
      const updated = await issuesApi.updateIssue(issueId!, {
        ...data,
        description: stripPendingInlineMedia(data.description),
      })
      const finalDescription = await finalizeIssueAttachmentsAndInlineMedia({
        issueId: issueId!,
        description: data.description,
        attachments: newAttachments,
        inlineMedia,
      })
      if (finalDescription !== data.description) {
        return issuesApi.updateIssue(issueId!, { description: finalDescription })
      }
      return updated
    },
    onSuccess: async (response) => {
      const updatedIssue = response.data.data
      qc.setQueryData(['issue', issueId], response)
      syncIssueAcrossProjectCaches(qc, updatedIssue.projectId, updatedIssue)
      setEditIssueOpen(false)
      await Promise.all([
        invalidateIssueViews(qc, updatedIssue.projectId),
        qc.invalidateQueries({ queryKey: ['issue', issueId] }),
        qc.invalidateQueries({ queryKey: ['attachments', issueId] }),
        qc.invalidateQueries({ queryKey: ['activity', issueId] }),
      ])
    },
  })

  const deleteIssueMutation = useMutation({
    mutationFn: (targetIssueId: string) => issuesApi.deleteIssue(targetIssueId),
    onSuccess: async (_, deletedIssueId) => {
      await Promise.all([
        invalidateIssueViews(qc, issue?.projectId),
        qc.invalidateQueries({ queryKey: ['sub-issues', issueId] }),
        qc.invalidateQueries({ queryKey: ['activity', issueId] }),
      ])

      if (deletedIssueId === issueId) {
        navigate(-1)
      }
    },
  })

  const readyForNextStageMutation = useMutation({
    mutationFn: ({ targetStatusId, targetStatusName, labelIds, timeLog, proof }: {
      targetStatusId: string
      targetStatusName: string
      labelIds?: string[]
      timeLog?: Parameters<typeof issuesApi.readyForNextStage>[1]['timeLog']
      proof?: Parameters<typeof submitStatusTransitionWithInternalNote>[0]['payload']['proof']
    }) => submitStatusTransitionWithInternalNote({
      issueId: issueId!,
      issueTitle: issue!.title,
      targetStatusName,
      payload: { targetStatusId, labelIds, timeLog, proof },
    }),
    onSuccess: async (response) => {
      const updatedIssue = response.data.data
      qc.setQueryData(['issue', issueId], response)
      syncIssueAcrossProjectCaches(qc, updatedIssue.projectId, updatedIssue)
      setChangeStatusOpen(false)
      await Promise.all([
        invalidateIssueViews(qc, updatedIssue.projectId),
        qc.invalidateQueries({ queryKey: ['issue', issueId] }),
        qc.invalidateQueries({ queryKey: ['time-logs', issueId] }),
        qc.invalidateQueries({ queryKey: ['comments', issueId] }),
        qc.invalidateQueries({ queryKey: ['activity', issueId] }),
        qc.invalidateQueries({ queryKey: ['attachments', issueId] }),
      ])
      invalidateWorkloadViews(qc)
    },
  })

  const statusChangeMutation = useMutation({
    mutationFn: (targetStatusId: string) =>
      issuesApi.reorderIssue(issue!.projectId, {
        issueId: issue!.id,
        targetStatusId,
        targetIndex: 999999,
      }),
    onSuccess: async () => {
      setChangeStatusOpen(false)
      await Promise.all([
        invalidateIssueViews(qc, issue!.projectId),
        qc.invalidateQueries({ queryKey: ['issue', issueId] }),
        qc.invalidateQueries({ queryKey: ['activity', issueId] }),
      ])
    },
  })

  if (isLoading) {
    return <div style={{ padding: 48, color: '#8a8f98' }}>Loading...</div>
  }

  if (!issue) {
    return (
      <div style={{ padding: 48 }}>
        <p style={{ color: '#8a8f98' }}>Issue not found</p>
        <Button variant="secondary" onClick={() => navigate(-1)}>Go back</Button>
      </div>
    )
  }

  const comments = commentsData?.data?.data || []
  const activity = activityData?.data?.data || []
  const attachments = attachmentsData?.data?.data || []
  const timeLogs = timeLogsData?.data?.data || []
  const statuses = statusesData?.data?.data || []
  const editIssueStatuses = statuses.filter(
    (status) => (!isReadyForNextStageStatus(status.name) && !isDoneStatus(status.name)) || status.id === issue.statusId
  )
  const members = membersData?.data?.data || []
  const labels = labelsData?.data?.data || []
  const subIssues = subIssuesData?.data?.data || []
  const isClient = user?.role === 'CLIENT'
  const isLeader = hasFullAccess(user?.role)
  const canViewAllTimeLogs = isLeader || isTesterRole(user?.role)
  const canEditIssue = canEditIssueForRole(user?.role, issue, user?.id)
  const canChangeStatus = canMoveIssue(user?.role, issue, user?.id)
  const canCreateSubIssue = canCreateSubIssueForRole(user?.role)
  const canDeleteCurrentIssue = canDeleteIssueForRole(user?.role, issue, user?.id)
  const canManageAssignments = isLeader
  const canCreateLabels = isLeader
  const canEditLabels = !!user && !isClient
  const canDeleteOwnComments = !!user
  const selectedStatus = statuses.find((status) => status.id === issue.statusId) || issue.status
  const selectedPriority = priorityOptions.find((priority) => priority.value === issue.priority)
  const creator = (issue as Issue & { createdBy?: User }).createdBy || user
  const visibleTimeLogs = canViewAllTimeLogs ? timeLogs : timeLogs.filter((entry) => entry.userId === user?.id)
  const currentLabelIds = issue.labels.map(({ label }) => label.id)
  const selectedAssigneeIds = issue.assignees.map((assignee) => assignee.user.id)
  const searchText = assigneeSearch.trim().toLowerCase()
  const matchesSearch = (person: User) =>
    !searchText ||
    person.fullName.toLowerCase().includes(searchText) ||
    person.email.toLowerCase().includes(searchText)
  const selectedPickerPeople = issue.assignees.map((assignee) => assignee.user).filter(matchesSearch)
  const availablePickerPeople = members
    .map((member) => member.user)
    .filter((member) => !selectedAssigneeIds.includes(member.id))
    .filter(matchesSearch)
  const issueWorkEstimateMap = Object.fromEntries(
    WORK_TYPE_OPTIONS.map((option) => [
      option.value,
      issue.workEstimates?.find((estimate) => estimate.workType === option.value)?.hours || 0,
    ])
  ) as Record<WorkType, number>
  const timeLogHours = toTimeLogHours(logHoursValue, logMinutesValue)
  const progressPercentNumber = parseInt(logProgressPercent || '0', 10)
  const isProgressingLogValid =
    logStatus === 'PROGRESSING' &&
    Number.isInteger(progressPercentNumber) &&
    progressPercentNumber >= 1 &&
    progressPercentNumber <= 99 &&
    !!logProgressNotes.trim()
  const isCompletedLogValid = logStatus === 'COMPLETED' && !!logCompletedDetails.trim()
  const isTimeLogValid =
    timeLogHours > 0 &&
    !!logDate &&
    !!logWorkType &&
    !!logStatus &&
    (isProgressingLogValid || isCompletedLogValid)

  const memberUsers = members.map((member) => member.user)
  const labelOptions = labels.map((label) => ({ id: label.id, name: label.name }))
  const statusOptions = statuses.map((status) => ({ id: status.id, name: status.name }))
  const activityItems = activity.map((entry) => ({
    id: `activity-${entry.id}`,
    createdAt: entry.createdAt,
    actor: entry.user,
    message: formatActivityMessage(entry, {
      members: memberUsers,
      labels: labelOptions,
      statuses: statusOptions,
    }),
  }))

  const updateAssignees = (userIds: string[]) => {
    updateMutation.mutate({ assigneeIds: userIds })
  }

  const toggleAssignee = (userId: string) => {
    const nextIds = selectedAssigneeIds.includes(userId)
      ? selectedAssigneeIds.filter((id) => id !== userId)
      : [...selectedAssigneeIds, userId]
    updateAssignees(nextIds)
  }

  const updateLabels = (labelIds: string[]) => {
    updateMutation.mutate({ labelIds })
  }

  const handleFiles = (files: FileList | null) => {
    if (!files?.length) return
    uploadFiles(Array.from(files))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const uploadFiles = (files: File[]) => {
    if (isClient) return
    if (files.length === 0) return
    const rejected = files.filter((file) => !isAllowedAttachmentFile(file))
    if (rejected.length > 0) {
      window.alert(getAllowedAttachmentMessage())
    }

    files
      .filter(isAllowedAttachmentFile)
      .forEach((file) => uploadMutation.mutate(file))
  }

  const handleComposerPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(event.clipboardData.files)
    if (files.length > 0) {
      event.preventDefault()
      const inlineFiles = files.filter(isInlineMediaFile)
      const attachmentFiles = files.filter((file) => !isInlineMediaFile(file))
      if (inlineFiles.length > 0) queueCommentInlineMediaFiles(inlineFiles)
      if (attachmentFiles.length > 0) uploadFiles(attachmentFiles)
      return
    }

    window.requestAnimationFrame(syncCommentFromEditor)
  }

  const handleComposerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return
    event.preventDefault()
    const inlineFiles = files.filter(isInlineMediaFile)
    const attachmentFiles = files.filter((file) => !isInlineMediaFile(file))
    if (inlineFiles.length > 0) queueCommentInlineMediaFiles(inlineFiles)
    if (attachmentFiles.length > 0) uploadFiles(attachmentFiles)
  }

  const syncCommentFromEditor = () => {
    if (!commentEditorRef.current) return
    setComment(normalizeRichTextEditorValue(commentEditorRef.current.innerHTML))
  }

  const runCommentEditorCommand = (command: string, value?: string) => {
    if (!commentEditorRef.current) return
    commentEditorRef.current.focus()
    document.execCommand(command, false, value)
    syncCommentFromEditor()
  }

  const insertCommentLink = () => {
    const url = window.prompt('Paste link URL')
    if (!url?.trim()) return
    runCommentEditorCommand('createLink', url.trim())
  }

  const insertCommentInlineImage = (draft: InlineMediaDraft) => {
    if (!commentEditorRef.current) return
    commentEditorRef.current.focus()
    const imageHtml = `<p><img src="pm-pending://${draft.token}" alt="${draft.label.replace(/"/g, '&quot;')}" data-width="480" style="width:480px;max-width:100%;height:auto;display:block;margin:10px 0;border-radius:12px;border:1px solid #dbe3ee;background:#f8fafc;" /></p><p><br></p>`
    document.execCommand('insertHTML', false, imageHtml)
    window.requestAnimationFrame(syncCommentFromEditor)
  }

  const queueCommentInlineMediaFiles = (files: File[]) => {
    const drafts = files
      .filter(isInlineMediaFile)
      .map((file) => createInlineMediaDraft(file))
      .filter((draft): draft is InlineMediaDraft => draft !== null)

    if (drafts.length === 0) return

    setCommentInlineMedia((current) => [...current, ...drafts])
    drafts.forEach(insertCommentInlineImage)
  }

  const handleCommentInlineImagePick = (files: FileList | null) => {
    if (!files) return
    queueCommentInlineMediaFiles(Array.from(files))
    if (commentInlineImageInputRef.current) {
      commentInlineImageInputRef.current.value = ''
    }
  }

  const resetTimeModal = () => {
    setLogOpen(false)
    setLogUserId(user?.id || '')
    setLogDate(todayDateInput())
    setLogWorkType('')
    setLogStatus('')
    setLogHoursValue('0')
    setLogMinutesValue('0')
    setLogProgressPercent('')
    setLogProgressNotes('')
    setLogCompletedDetails('')
    setEditingTimeLog(null)
  }

  const openLogTime = (entry?: TimeLog) => {
    const split = entry ? splitTimeLogHours(entry.hours) : { hours: '0', minutes: '0' }
    setLogUserId(user?.id || '')
    setLogDate(entry ? entry.loggedDate.split('T')[0] : todayDateInput())
    setLogWorkType(entry?.workType || '')
    setLogStatus(entry?.status || '')
    setLogHoursValue(split.hours)
    setLogMinutesValue(split.minutes)
    setLogProgressPercent(
      entry?.status === 'PROGRESSING' && typeof entry.progressPercent === 'number'
        ? String(entry.progressPercent)
        : ''
    )
    setLogProgressNotes(entry?.progressNotes || '')
    setLogCompletedDetails(entry?.completedDetails || '')
    setEditingTimeLog(entry || null)
    setLogOpen(true)
  }

  const handleSubmitComment = () => {
    if (!hasRichTextContent(comment) || commentMutation.isPending) return
    if (
      canPostInternalComment &&
      !isInternal &&
      !window.confirm('This comment will be shared with the customer. Do you want to proceed?')
    ) {
      return
    }
    commentMutation.mutate(comment)
  }

  const handleCopyLink = async () => {
    try {
      await copyTextToClipboard(window.location.href)
      setCopyLinkState('copied')
      window.setTimeout(() => setCopyLinkState('idle'), 2000)
    } catch {
      window.alert('Could not copy the issue link from this browser.')
    }
  }

  const handleDeleteIssue = (targetIssueId: string, label: string) => {
    if (!window.confirm(`Delete ${label}?`)) return
    deleteIssueMutation.mutate(targetIssueId)
  }

  const scrollToAttachments = () => {
    attachmentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div style={pageStyle}>
      <div style={topBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <button onClick={() => navigate(-1)} style={topIconStyle} title="Close">
            <X size={20} />
          </button>
          <div style={dividerStyle} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, paddingLeft: 16 }}>
            <span style={{ fontSize: 14, color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {issue.identifier}
            </span>
            <button
              type="button"
              onClick={handleCopyLink}
              style={shareLinkButtonStyle}
              title={copyLinkState === 'copied' ? 'Link copied' : 'Copy issue link'}
            >
              <Link2 size={13} />
              {copyLinkState === 'copied' ? 'Copied' : 'Share link'}
            </button>
            {attachments.length > 0 && (
              <button
                type="button"
                onClick={scrollToAttachments}
                style={attachmentIndicatorStyle}
                title={`${attachments.length} attachment${attachments.length > 1 ? 's' : ''}`}
              >
                <Paperclip size={13} />
                {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {canChangeStatus && (
            <button
              type="button"
              onClick={() => setChangeStatusOpen(true)}
              style={changeStatusButtonStyle}
              title="Change status"
            >
              Change status
            </button>
          )}
          {canEditIssue && (
            <button style={topIconStyle} title="Edit issue" onClick={() => setEditIssueOpen(true)}>
              <Edit2 size={18} />
            </button>
          )}
          {canDeleteCurrentIssue && (
            <button
              style={{ ...topIconStyle, color: '#dc2626' }}
              title="Delete issue"
              onClick={() => handleDeleteIssue(issue.id, `issue ${issue.identifier}`)}
            >
              <Trash2 size={18} />
            </button>
          )}
          <button style={topIconStyle} title="More"><MoreHorizontal size={18} /></button>
          <button
            style={topIconStyle}
            title={copyLinkState === 'copied' ? 'Link copied' : 'Copy link'}
            onClick={handleCopyLink}
          >
            <Link2 size={18} />
          </button>
          <button style={topIconStyle} title="Settings"><Settings size={18} /></button>
          <button style={topIconStyle} title="Layout"><Columns3 size={18} /></button>
          <button style={{ ...topIconStyle, background: '#f3f4f6', color: '#111827' }} title="Details"><Grid3X3 size={18} /></button>
        </div>
      </div>

      <div style={bodyStyle}>
        <main style={mainStyle}>
          <section style={{ maxWidth: 860, margin: '0 auto', padding: '24px 0 64px' }}>
            <h1 style={titleStyle}>{issue.title}</h1>
            {issue.description ? (
              <IssueDescriptionContent description={issue.description} style={descriptionStyle} />
            ) : (
              <div style={{ ...descriptionStyle, color: '#a1a1aa' }}>No description</div>
            )}

            {!isClient && (
              <div style={actionRailStyle}>
                <input ref={fileInputRef} type="file" multiple accept={ALLOWED_ATTACHMENT_ACCEPT} onChange={(event) => handleFiles(event.target.files)} style={{ display: 'none' }} />
                <ToolbarButton icon={<Paperclip size={17} />} onClick={() => fileInputRef.current?.click()} label="Attach files" />
              </div>
            )}

            {attachments.length > 0 && (
              <div ref={attachmentSectionRef} style={attachmentListStyle}>
                {attachments.map((attachment) => (
                  <div key={attachment.id} style={attachmentCardStyle}>
                    {attachment.mimeType?.startsWith('image/') || attachment.mimeType?.startsWith('video/') ? (
                      <AttachmentPreview
                        filename={attachment.filename}
                        fileUrl={attachment.fileUrl}
                        mimeType={attachment.mimeType}
                      />
                    ) : (
                      <div style={attachmentThumbStyle}><Paperclip size={18} /></div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {attachment.filename}
                      </div>
                      <div style={{ fontSize: 12, color: '#8a8f98' }}>{(attachment.fileSize / 1024).toFixed(1)}kb</div>
                    </div>
                    <a href={getAttachmentFileUrl(attachment.fileUrl)} target="_blank" rel="noreferrer" style={smallIconStyle} title="Open">
                      <ExternalLink size={15} />
                    </a>
                    {(attachment.uploadedBy.id === user?.id || isLeader) && (
                      <button onClick={() => deleteAttachmentMutation.mutate(attachment.id)} style={smallIconStyle} title="Delete">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {canCreateSubIssue && (
              <button style={subIssueButtonStyle} onClick={() => setSubIssueOpen(true)}>
                <span style={{ fontSize: 22, lineHeight: 1 }}>+</span>
                Add sub-issue
              </button>
            )}

            {subIssues.length > 0 && (
              <div style={subIssueListStyle}>
                {subIssues.map((subIssue) => (
                  <div key={subIssue.id} style={subIssueRowWrapStyle}>
                    <button onClick={() => navigate(`/issues/${subIssue.id}`)} style={subIssueRowStyle}>
                      <Circle size={14} style={{ color: subIssue.status?.color || '#94a3b8' }} />
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#8a8f98' }}>{subIssue.identifier}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subIssue.title}</span>
                    </button>
                    {canDeleteIssueForRole(user?.role, subIssue, user?.id) && (
                      <button
                        type="button"
                        onClick={() => handleDeleteIssue(subIssue.id, `sub-issue ${subIssue.identifier}`)}
                        style={subIssueDeleteButtonStyle}
                        title="Delete sub-issue"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={activityHeaderStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, color: '#1f2937' }}>
                <CheckSquare size={16} />
                Conversation
              </div>
            </div>

            <div style={composerStyle}>
              <input
                ref={commentInlineImageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                onChange={(event) => handleCommentInlineImagePick(event.target.files)}
                style={{ display: 'none' }}
              />
              <div style={composerToolbarStyle}>
                <div style={compactToolbarGroupStyle}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runCommentEditorCommand('bold')}
                    style={composerToolbarButtonStyle}
                  >
                    <Bold size={15} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runCommentEditorCommand('italic')}
                    style={composerToolbarButtonStyle}
                  >
                    <Italic size={15} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runCommentEditorCommand('underline')}
                    style={composerToolbarButtonStyle}
                  >
                    <Underline size={15} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runCommentEditorCommand('strikeThrough')}
                    style={composerToolbarButtonStyle}
                  >
                    <Strikethrough size={15} />
                  </button>
                  <span style={toolbarDividerStyle} />
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runCommentEditorCommand('insertUnorderedList')}
                    style={composerToolbarButtonStyle}
                  >
                    <List size={15} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runCommentEditorCommand('insertOrderedList')}
                    style={composerToolbarButtonStyle}
                  >
                    <ListOrdered size={15} />
                  </button>
                  <span style={toolbarDividerStyle} />
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={insertCommentLink}
                    style={composerToolbarButtonStyle}
                  >
                    <Link2 size={15} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => commentInlineImageInputRef.current?.click()}
                    style={composerToolbarButtonStyle}
                  >
                    <ImagePlus size={14} />
                  </button>
                </div>
              </div>
              <div style={composerEditorWrapStyle}>
                {!hasRichTextContent(comment) && (
                  <div style={composerPlaceholderStyle}>Start typing...</div>
                )}
                <div
                  ref={commentEditorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncCommentFromEditor}
                  onBlur={syncCommentFromEditor}
                  onPaste={handleComposerPaste}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleComposerDrop}
                  style={composerInputStyle}
                />
              </div>
              <div style={composerFooterStyle}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <ToolbarButton icon={<Paperclip size={16} />} onClick={() => fileInputRef.current?.click()} label="Attach files" />
                  {!isClient && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 10, fontSize: 12, color: '#6b7280' }}>
                      <input type="checkbox" checked={isInternal} onChange={(event) => setIsInternal(event.target.checked)} />
                      Internal
                    </label>
                  )}
                </div>
                <button
                  onClick={handleSubmitComment}
                  disabled={!hasRichTextContent(comment) || commentMutation.isPending}
                  style={{ ...sendButtonStyle, opacity: hasRichTextContent(comment) ? 1 : 0.35 }}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>

            <div style={{ paddingTop: 28 }}>
              {comments.map((entry) => (
                <div key={entry.id} style={activityItemStyle}>
                  <Avatar user={entry.user} size="lg" />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong style={{ fontSize: 14 }}>{entry.user.fullName}</strong>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{format(new Date(entry.createdAt), 'dd MMM yyyy, HH:mm')}</span>
                      {entry.isInternal && <span style={badgeStyle}>Internal</span>}
                      {canDeleteOwnComments && entry.userId === user?.id && (
                        <button
                          type="button"
                          onClick={() => {
                            if (!window.confirm('Delete this comment?')) return
                            deleteCommentMutation.mutate(entry.id)
                          }}
                          disabled={deleteCommentMutation.isPending}
                          style={commentDeleteButtonStyle}
                          title="Delete comment"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    <IssueDescriptionContent
                      description={entry.content}
                      style={{
                        marginTop: 7,
                        fontSize: 14,
                        color: '#111827',
                        whiteSpace: 'pre-wrap',
                      }}
                    />
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <div style={{ fontSize: 13, color: '#8a8f98' }}>No conversation yet.</div>
              )}
            </div>

            <div style={{ ...activityHeaderStyle, marginTop: 36 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, color: '#1f2937' }}>
                <CheckSquare size={16} />
                Activity Log
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button style={filterButtonStyle}>All</button>
              </div>
            </div>

            <div style={{ paddingTop: 28 }}>
              {activityItems.map((entry) => (
                <div key={entry.id} style={activityItemStyle}>
                  <Avatar user={entry.actor} size="lg" />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong style={{ fontSize: 14 }}>{entry.actor.fullName}</strong>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{format(new Date(entry.createdAt), 'dd MMM yyyy, HH:mm')}</span>
                      <span style={badgeStyle}>System log</span>
                    </div>
                    <div
                      style={{
                        marginTop: 7,
                        fontSize: 14,
                        color: '#111827',
                        whiteSpace: 'pre-wrap',
                        fontStyle: 'italic',
                      }}
                    >
                      {entry.message}
                    </div>
                  </div>
                </div>
              ))}
              {activityItems.length === 0 && (
                <div style={{ fontSize: 13, color: '#8a8f98' }}>No activity yet.</div>
              )}
            </div>
          </section>
        </main>

        <aside style={sidePanelStyle}>
          <PanelSection>
            <PropertyRow label="Template">
              <span>Smoke Test</span>
            </PropertyRow>
            <PropertyRow label="Status">
              <span style={mutedInlineStyle}>
                <Circle size={14} style={{ color: selectedStatus?.color || '#94a3b8' }} />
                {selectedStatus?.name || 'Status'}
              </span>
            </PropertyRow>
            <PropertyRow label="Priority">
                <InlineSelect
                  value={issue.priority}
                disabled={!canEditIssue}
                onChange={(value) => updateMutation.mutate({ priority: value as Issue['priority'] })}
                options={priorityOptions.map((priority) => ({ value: priority.value, label: priority.label }))}
                icon={<Menu size={14} />}
                label={selectedPriority?.label || 'No priority'}
              />
            </PropertyRow>
            <PropertyRow label="Created by">
              {creator ? <AvatarText user={creator} /> : <span>-</span>}
            </PropertyRow>
            <PropertyRow label="Assignee">
              <div ref={assigneePickerRef} style={assigneePickerWrapStyle}>
                <button
                  type="button"
                  onClick={() => canManageAssignments && setAssigneePickerOpen((open) => !open)}
                  disabled={!canManageAssignments}
                  style={assigneePickerButtonStyle}
                >
                  {issue.assignees.length > 0 ? issue.assignees.map((assignee) => (
                    <Avatar key={assignee.user.id} user={assignee.user} size="sm" style={{ marginLeft: -4, border: '2px solid #fff' }} />
                  )) : <span style={{ color: '#8a8f98' }}>Select person</span>}
                </button>

                {canManageAssignments && assigneePickerOpen && (
                  <div style={assigneePopoverStyle}>
                    <div style={assigneeSearchStyle}>
                      <Search size={16} />
                      <input
                        autoFocus
                        value={assigneeSearch}
                        onChange={(event) => setAssigneeSearch(event.target.value)}
                        placeholder="Search..."
                        style={assigneeSearchInputStyle}
                      />
                    </div>
                    {selectedPickerPeople.length > 0 && (
                      <>
                        <div style={pickerGroupLabelStyle}>SELECTED</div>
                        {selectedPickerPeople.map((person) => (
                          <PersonPickerRow key={person.id} person={person} selected onToggle={() => toggleAssignee(person.id)} />
                        ))}
                      </>
                    )}
                    <div style={pickerGroupLabelStyle}>PERSON</div>
                    {availablePickerPeople.length > 0 ? availablePickerPeople.map((person) => (
                      <PersonPickerRow key={person.id} person={person} selected={false} onToggle={() => toggleAssignee(person.id)} />
                    )) : (
                      <div style={emptyPickerStyle}>No people found</div>
                    )}
                  </div>
                )}
              </div>
            </PropertyRow>
            <PropertyRow label="Labels">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
                {issue.labels.length > 0 ? issue.labels.map(({ label }) => (
                  <span key={label.id} style={labelChipStyle}>
                    <span style={{ width: 7, height: 7, borderRadius: 99, background: label.color }} />
                    {label.name}
                    {canEditLabels && (
                      <button
                        onClick={() => updateLabels(currentLabelIds.filter((id) => id !== label.id))}
                        style={chipRemoveStyle}
                        title="Remove label"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </span>
                )) : <span>-</span>}
              </div>
            </PropertyRow>
            {canEditLabels && (
              <div style={labelToolsStyle}>
                <select
                  value=""
                  onChange={(event) => {
                    if (!event.target.value) return
                    updateLabels(Array.from(new Set([...currentLabelIds, event.target.value])))
                  }}
                  style={miniSelectStyle}
                >
                  <option value="">Select label</option>
                  {labels.filter((label) => !currentLabelIds.includes(label.id)).map((label) => (
                    <option key={label.id} value={label.id}>{label.name}</option>
                  ))}
                </select>
                {canCreateLabels && (
                  <div style={newLabelStyle}>
                    <input
                      value={newLabelName}
                      onChange={(event) => setNewLabelName(event.target.value)}
                      placeholder="New label"
                      style={miniInputStyle}
                    />
                    <button
                      onClick={() => newLabelName.trim() && createLabelMutation.mutate(newLabelName.trim())}
                      disabled={!newLabelName.trim() || createLabelMutation.isPending}
                      style={miniButtonStyle}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
            )}
          </PanelSection>

          <PanelSection>
            <PropertyRow label="Component">
              <span style={mutedInlineStyle}><Diamond size={14} /> {issue.component?.name || 'No component'}</span>
            </PropertyRow>
            <PropertyRow label="Milestone">
              <span style={mutedInlineStyle}><FlagIcon /> {issue.milestone?.name || 'Milestone'}</span>
            </PropertyRow>
            <PropertyRow label="Due">
              <span style={mutedInlineStyle}>
                <Calendar size={14} />
                {issue.dueDate ? format(new Date(issue.dueDate), 'd MMM yyyy') : 'No due date'}
              </span>
            </PropertyRow>
          </PanelSection>

          {!isClient && ((issue.estimatedHours ?? 0) > 0 || Object.values(issueWorkEstimateMap).some(v => v > 0)) && (
          <PanelSection>
            <div style={trackingTitleRowStyle}>
              <div style={trackingTitleStyle}>Estimated Work Plan</div>
              <div style={{ fontSize: 12.5, color: '#6b7280' }}>{issue.estimatedHours ?? 0}h total</div>
            </div>
            <div style={estimateBreakdownListStyle}>
              {WORK_TYPE_OPTIONS.filter((option) => (issueWorkEstimateMap[option.value] || 0) > 0).map((option) => (
                <div key={option.value} style={estimateBreakdownRowStyle}>
                  <span>{formatWorkType(option.value)}</span>
                  <strong>{issueWorkEstimateMap[option.value]}h</strong>
                </div>
              ))}
            </div>
          </PanelSection>
          )}

          <PanelSection>
            <PropertyRow label="Task type">
              <span style={mutedInlineStyle}><CheckSquare size={14} /> Issue</span>
            </PropertyRow>
            <div style={trackingTitleRowStyle}>
              <div style={trackingTitleStyle}>Activity Time</div>
              {!isClient && (
                <button
                  onClick={() => openLogTime()}
                  style={trackingHeaderAddButtonStyle}
                >
                  + Add time
                </button>
              )}
            </div>
          </PanelSection>

          <PanelSection>
            <PropertyRow label="Collaborators">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex' }}>
                  {members.slice(0, 4).map((member) => (
                    <Avatar key={member.userId} user={member.user} size="sm" style={{ marginLeft: -5, border: '2px solid #fff' }} />
                  ))}
                </div>
                <span>{members.length} members</span>
              </div>
            </PropertyRow>
          </PanelSection>

          <PanelSection>
            <div style={trackingTitleStyle}>Time Tracking History</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {visibleTimeLogs.map((entry) => (
                <div key={entry.id} style={historyRowStyle} title={getTimeLogHoverText(entry)}>
                  <span title={format(new Date(entry.createdAt), 'dd MMM yyyy, HH:mm')}>
                    {format(new Date(entry.loggedDate), 'dd MMM yyyy')}
                    <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 4 }}>
                      {format(new Date(entry.createdAt), 'HH:mm')}
                    </span>
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.user.fullName}
                    <span style={{ color: '#94a3b8' }}> · {formatWorkType(entry.workType)}</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {formatLoggedHours(entry.hours)}
                    {entry.user.id === user?.id && (
                      <>
                        <button onClick={() => openLogTime(entry)} style={historyIconButtonStyle} title="Edit time">
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => deleteTimeLogMutation.mutate(entry.id)} style={historyDeleteButtonStyle} title="Delete time">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </span>
                </div>
              ))}
              {visibleTimeLogs.length === 0 && (
                <div style={{ fontSize: 12.5, color: '#8a8f98' }}>No time entries yet</div>
              )}
            </div>
          </PanelSection>
        </aside>
      </div>

      <Modal isOpen={logOpen} onClose={resetTimeModal} width={360}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={timeModalHeaderStyle}>
            <h2 style={{ margin: 0, fontSize: 18, color: '#111827' }}>{editingTimeLog ? 'Edit Time Entry' : 'Add Time Entry'}</h2>
            <button onClick={resetTimeModal} style={topIconStyle} title="Close">
              <X size={18} />
            </button>
          </div>
          <div>
            <label style={formLabelStyle}>Employee Name</label>
            <input value={user?.fullName || ''} disabled style={{ ...formInputStyle, background: '#f3f4f6' }} />
          </div>
          <div>
            <label style={formLabelStyle}>Date</label>
            <input type="date" value={logDate} onChange={(event) => setLogDate(event.target.value)} style={formInputStyle} />
          </div>
          <div>
            <label style={formLabelStyle}>Task</label>
            <input value={`${issue.identifier} · ${issue.title}`} disabled style={{ ...formInputStyle, background: '#f3f4f6' }} />
          </div>
          <div>
            <label style={formLabelStyle}>Work Type</label>
            <select value={logWorkType} onChange={(event) => setLogWorkType(event.target.value as TimeLogWorkTypeValue)} style={formInputStyle}>
              <option value="" disabled>Please select</option>
              {WORK_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={formLabelStyle}>Hours</label>
              <input type="number" min="0" max="24" step="1" value={logHoursValue} onChange={(event) => setLogHoursValue(event.target.value)} style={formInputStyle} />
            </div>
            <div>
              <label style={formLabelStyle}>Minutes</label>
              <input type="number" min="0" max="59" step="1" value={logMinutesValue} onChange={(event) => setLogMinutesValue(event.target.value)} style={formInputStyle} />
            </div>
          </div>
          <div>
            <label style={formLabelStyle}>Status *</label>
            <select value={logStatus} onChange={(event) => setLogStatus(event.target.value as TimeLogStatusValue)} style={formInputStyle}>
              <option value="" disabled>Please select</option>
              <option value="PROGRESSING">Progressing</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
          {logStatus === 'PROGRESSING' ? (
            <>
              <div>
                <label style={formLabelStyle}>Progress % *</label>
                <input
                  type="number"
                  min="1"
                  max="99"
                  step="1"
                  value={logProgressPercent}
                  onChange={(event) => setLogProgressPercent(event.target.value)}
                  placeholder="Enter progress percent"
                  style={formInputStyle}
                />
              </div>
              <div>
                <label style={formLabelStyle}>Progress Notes *</label>
                <textarea
                  value={logProgressNotes}
                  onChange={(event) => setLogProgressNotes(event.target.value)}
                  rows={5}
                  placeholder={`Write what was progressed so far as a short list.\nExample:\n- API 1 completed\n- API 2 completed\n- UI validation started\n- Login error handling pending`}
                  style={{ ...formInputStyle, resize: 'vertical', minHeight: 120 }}
                />
              </div>
            </>
          ) : (
            <div>
              <label style={formLabelStyle}>Completed Details *</label>
              <textarea
                value={logCompletedDetails}
                onChange={(event) => setLogCompletedDetails(event.target.value)}
                rows={5}
                placeholder={`Write what was completed in this session.\nExample:\n- API 1 completed\n- API 2 completed\n- Backend validation completed\n- FE integration completed`}
                style={{ ...formInputStyle, resize: 'vertical', minHeight: 120 }}
              />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Button variant="secondary" onClick={resetTimeModal}>
              Cancel
            </Button>
            <Button
              disabled={!isTimeLogValid}
              loading={timeLogMutation.isPending || updateTimeLogMutation.isPending}
              onClick={() => editingTimeLog ? updateTimeLogMutation.mutate() : timeLogMutation.mutate()}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      <ReadyForNextStageModal
        isOpen={changeStatusOpen}
        issue={issue}
        statuses={statuses}
        labels={labels}
        user={user}
        isSubmitting={readyForNextStageMutation.isPending || statusChangeMutation.isPending}
        onClose={() => setChangeStatusOpen(false)}
        onSubmit={({ targetStatusId, labelIds, timeLog, proof }) => {
          const nextStatus = statuses.find((status) => status.id === targetStatusId)
          if (!nextStatus) return

          if (requiresStatusDetailsPopup({
            currentStatusName: issue.status?.name,
            nextStatusName: nextStatus.name,
            userRole: user?.role,
          })) {
            readyForNextStageMutation.mutate({
              targetStatusId,
              targetStatusName: nextStatus.name,
              ...(labelIds ? { labelIds } : {}),
              ...(timeLog ? { timeLog } : {}),
              ...(proof ? { proof } : {}),
            })
            return
          }

          statusChangeMutation.mutate(targetStatusId)
        }}
      />

      {editIssueOpen && statuses.length > 0 && (
        <IssueForm
          isOpen={editIssueOpen}
          onClose={() => setEditIssueOpen(false)}
          projectId={issue.projectId}
          statuses={editIssueStatuses}
          defaultStatusId={issue.statusId}
          initialData={issue}
          onSubmit={(data) => editIssueMutation.mutate(data)}
          isSubmitting={editIssueMutation.isPending}
          title="Edit issue"
        />
      )}

      {statuses.length > 0 && (
        <IssueForm
          isOpen={subIssueOpen}
          onClose={() => setSubIssueOpen(false)}
          projectId={issue.projectId}
          statuses={statuses}
          defaultStatusId={issue.statusId}
          onSubmit={(data) => createSubIssueMutation.mutate(data)}
          isSubmitting={createSubIssueMutation.isPending}
          title="New sub-issue"
        />
      )}
    </div>
  )
}

function ToolbarButton({ icon, onClick, label }: { icon: React.ReactNode; onClick?: () => void; label: string }) {
  return (
    <button onClick={onClick} style={toolbarButtonStyle} title={label} aria-label={label}>
      {icon}
    </button>
  )
}

function AttachmentPreview({
  filename,
  fileUrl,
  mimeType,
}: {
  filename: string
  fileUrl: string
  mimeType: string
}) {
  const [failed, setFailed] = useState(false)
  const src = getAttachmentFileUrl(fileUrl)

  if (failed) {
    return (
      <div style={attachmentThumbStyle}>
        <Paperclip size={18} />
      </div>
    )
  }

  if (mimeType.startsWith('video/')) {
    return (
      <video
        src={src}
        style={attachmentThumbStyle}
        muted
        playsInline
        preload="metadata"
        onError={() => setFailed(true)}
      />
    )
  }

  return <img src={src} alt={filename} style={attachmentThumbStyle} onError={() => setFailed(true)} />
}

function PanelSection({ children }: { children: React.ReactNode }) {
  return <div style={panelSectionStyle}>{children}</div>
}

function PropertyRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={propertyRowStyle}>
      <div style={propertyLabelStyle}>{label}</div>
      <div style={propertyValueStyle}>{children}</div>
    </div>
  )
}

function InlineSelect({
  value,
  label,
  icon,
  options,
  disabled,
  onChange,
}: {
  value: string
  label: string
  icon?: React.ReactNode
  options: { value: string; label: string }[]
  disabled?: boolean
  onChange: (value: string) => void
}) {
  return (
    <span style={inlineSelectStyle}>
      {icon}
      {label}
      {!disabled && (
        <select value={value} onChange={(event) => onChange(event.target.value)} style={overlaySelectStyle}>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      )}
    </span>
  )
}

function PersonPickerRow({ person, selected, onToggle }: { person: User; selected: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} style={{ ...personPickerRowStyle, background: selected ? '#f3f4f6' : '#ffffff' }}>
      <Avatar user={person} size="sm" />
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
        {person.fullName}
      </span>
      {selected && <Check size={15} style={{ color: '#6b7280', flexShrink: 0 }} />}
    </button>
  )
}

function AvatarText({ user }: { user: User }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Avatar user={user} size="sm" />
      {user.fullName}
    </span>
  )
}

function FlagIcon() {
  return <span style={{ width: 14, height: 14, borderLeft: '1.5px solid currentColor', borderTop: '1.5px solid currentColor', display: 'inline-block' }} />
}

const pageStyle: React.CSSProperties = {
  height: 'calc(100vh - 52px)',
  background: '#ffffff',
  color: '#111827',
  overflow: 'hidden',
}

const topBarStyle: React.CSSProperties = {
  height: 52,
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: 16,
}

const topIconStyle: React.CSSProperties = {
  width: 34,
  height: 34,
  border: 'none',
  background: 'transparent',
  color: '#525866',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
}

const dividerStyle: React.CSSProperties = {
  width: 1,
  height: 30,
  background: '#e5e7eb',
}

const bodyStyle: React.CSSProperties = {
  display: 'flex',
  height: 'calc(100% - 52px)',
  minHeight: 0,
}

const mainStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'auto',
  padding: '0 28px',
}

const sidePanelStyle: React.CSSProperties = {
  width: 372,
  minWidth: 372,
  borderLeft: '1px solid #e5e7eb',
  overflowY: 'auto',
  background: '#ffffff',
}

const titleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 500,
  color: '#111827',
  margin: '0 0 24px',
  lineHeight: 1.35,
}

const descriptionStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#2563eb',
  lineHeight: 1.6,
  marginBottom: 34,
}

const actionRailStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginBottom: 8,
}

const toolbarButtonStyle: React.CSSProperties = {
  width: 29,
  height: 29,
  border: 'none',
  background: 'transparent',
  borderRight: '1px solid #e5e7eb',
  color: '#374151',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const attachmentListStyle: React.CSSProperties = {
  border: '1px solid #dadde3',
  borderRadius: 5,
  marginBottom: 24,
}

const attachmentCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: 8,
  minHeight: 44,
}

const attachmentThumbStyle: React.CSSProperties = {
  width: 48,
  height: 36,
  objectFit: 'cover',
  background: '#f3f4f6',
  border: '1px solid #d1d5db',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#6b7280',
}

const smallIconStyle: React.CSSProperties = {
  width: 27,
  height: 27,
  border: 'none',
  background: 'transparent',
  color: '#4b5563',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 5,
}

const subIssueButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  margin: '0 0 14px 0',
  border: 'none',
  background: 'transparent',
  color: '#4b5563',
  cursor: 'pointer',
  fontSize: 14,
}

const subIssueListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginBottom: 30,
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  overflow: 'hidden',
}

const subIssueRowWrapStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'stretch',
  borderBottom: '1px solid #f1f5f9',
}

const subIssueRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '18px 78px minmax(0, 1fr)',
  gap: 8,
  alignItems: 'center',
  width: '100%',
  minHeight: 36,
  border: 'none',
  background: '#ffffff',
  color: '#1f2937',
  cursor: 'pointer',
  padding: '8px 10px',
  fontSize: 13,
  textAlign: 'left',
}

const subIssueDeleteButtonStyle: React.CSSProperties = {
  width: 34,
  border: 'none',
  background: '#ffffff',
  color: '#dc2626',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const activityHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 10,
}

const filterButtonStyle: React.CSSProperties = {
  border: '1px solid #dfe3ea',
  background: '#f9fafb',
  color: '#4b5563',
  borderRadius: 4,
  padding: '5px 10px',
  cursor: 'pointer',
}

const composerStyle: React.CSSProperties = {
  border: '1px solid #cfd4dc',
  borderRadius: 4,
  background: '#ffffff',
}

const composerToolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 8,
  padding: '8px 10px',
}

const compactToolbarGroupStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  border: '1px solid #b9c1cb',
  borderRadius: 4,
  background: '#ffffff',
  padding: '2px',
}

const composerToolbarButtonStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  border: 'none',
  background: 'transparent',
  color: '#334155',
  borderRadius: 4,
  padding: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const toolbarDividerStyle: React.CSSProperties = {
  width: 1,
  height: 18,
  background: '#dbe3ee',
  margin: '0 2px',
}

const composerEditorWrapStyle: React.CSSProperties = {
  position: 'relative',
  minHeight: 180,
  resize: 'vertical',
  overflow: 'auto',
  borderTop: '1px solid #e5e7eb',
}

const composerPlaceholderStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  left: 14,
  color: '#9ca3af',
  fontSize: 14,
  pointerEvents: 'none',
}

const composerInputStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  padding: '12px 14px',
  minHeight: 180,
  fontFamily: 'inherit',
  fontSize: 14,
  color: '#111827',
  boxSizing: 'border-box',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.5,
}

const composerFooterStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTop: '1px solid #eef0f3',
  padding: '3px 7px',
}

const sendButtonStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  border: 'none',
  background: 'transparent',
  color: '#6b7280',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const activityItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  marginBottom: 15,
  alignItems: 'flex-start',
}

const badgeStyle: React.CSSProperties = {
  fontSize: 11,
  background: '#fef3c7',
  color: '#92400e',
  borderRadius: 4,
  padding: '2px 6px',
}

const commentDeleteButtonStyle: React.CSSProperties = {
  marginLeft: 4,
  border: 'none',
  background: '#f3f4f6',
  color: '#6b7280',
  borderRadius: 999,
  padding: '3px 10px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
}

const panelSectionStyle: React.CSSProperties = {
  padding: '16px 26px',
  borderBottom: '1px solid #e5e7eb',
}

const propertyRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '116px minmax(0, 1fr)',
  gap: 14,
  alignItems: 'center',
  minHeight: 30,
  fontSize: 13,
}

const propertyLabelStyle: React.CSSProperties = {
  color: '#6b7280',
}

const propertyValueStyle: React.CSSProperties = {
  color: '#1f2937',
  minWidth: 0,
}

const inlineSelectStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 30,
  cursor: 'pointer',
}

const overlaySelectStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  cursor: 'pointer',
}

const labelChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  padding: '4px 8px',
  background: '#ffffff',
  maxWidth: '100%',
}

const chipRemoveStyle: React.CSSProperties = {
  width: 16,
  height: 16,
  border: 'none',
  background: 'transparent',
  color: '#6b7280',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
}

const labelToolsStyle: React.CSSProperties = {
  marginLeft: 130,
  marginTop: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
}

const assigneePickerWrapStyle: React.CSSProperties = {
  position: 'relative',
  minWidth: 0,
}

const assigneePickerButtonStyle: React.CSSProperties = {
  minHeight: 28,
  minWidth: 84,
  border: 'none',
  background: 'transparent',
  color: '#4b5563',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  cursor: 'pointer',
  padding: 0,
}

const assigneePopoverStyle: React.CSSProperties = {
  position: 'absolute',
  top: 34,
  left: -8,
  zIndex: 30,
  width: 246,
  maxHeight: 350,
  overflowY: 'auto',
  border: '1px solid #dfe3ea',
  borderRadius: 8,
  background: '#ffffff',
  boxShadow: '0 12px 26px rgba(15, 23, 42, 0.16)',
  padding: 8,
}

const assigneeSearchStyle: React.CSSProperties = {
  height: 38,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: '1px solid #93c5fd',
  borderRadius: 6,
  padding: '0 9px',
  color: '#6b7280',
  marginBottom: 10,
}

const assigneeSearchInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  fontSize: 13,
  fontFamily: 'inherit',
  color: '#111827',
}

const pickerGroupLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#111827',
  margin: '10px 8px 6px',
  textTransform: 'uppercase',
}

const personPickerRowStyle: React.CSSProperties = {
  width: '100%',
  height: 39,
  border: 'none',
  borderRadius: 5,
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '0 8px',
  color: '#1f2937',
  cursor: 'pointer',
  fontSize: 13,
}

const emptyPickerStyle: React.CSSProperties = {
  padding: '9px 8px 12px',
  fontSize: 12.5,
  color: '#8a8f98',
}

const miniSelectStyle: React.CSSProperties = {
  width: '100%',
  height: 31,
  border: '1px solid #e5e7eb',
  borderRadius: 5,
  padding: '0 8px',
  fontSize: 12.5,
  color: '#374151',
  background: '#ffffff',
  outline: 'none',
}

const newLabelStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 6,
}

const miniInputStyle: React.CSSProperties = {
  minWidth: 0,
  height: 31,
  border: '1px solid #e5e7eb',
  borderRadius: 5,
  padding: '0 8px',
  fontSize: 12.5,
  outline: 'none',
}

const miniButtonStyle: React.CSSProperties = {
  height: 31,
  border: '1px solid #d7dce3',
  borderRadius: 5,
  background: '#f9fafb',
  color: '#374151',
  cursor: 'pointer',
  padding: '0 10px',
  fontSize: 12.5,
}

const mutedInlineStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  color: '#4b5563',
}

const shareLinkButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 28,
  borderRadius: 999,
  border: '1px solid #dbe1ea',
  background: '#ffffff',
  color: '#4f46e5',
  fontSize: 12,
  fontWeight: 600,
  padding: '0 10px',
  cursor: 'pointer',
  flexShrink: 0,
}

const attachmentIndicatorStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 28,
  borderRadius: 999,
  border: '1px solid #dbe1ea',
  background: '#ffffff',
  color: '#475569',
  fontSize: 12,
  fontWeight: 600,
  padding: '0 10px',
  cursor: 'pointer',
  flexShrink: 0,
}

const changeStatusButtonStyle: React.CSSProperties = {
  minWidth: 146,
  height: 34,
  border: '1px solid #d7dce3',
  background: '#ffffff',
  color: '#111827',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  padding: '0 14px',
}

const trackingTitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#111827',
  fontWeight: 600,
  margin: 0,
}

const trackingTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  margin: '4px 0 12px',
}

const trackingHeaderAddButtonStyle: React.CSSProperties = {
  border: '1px solid #2563eb',
  background: '#eff6ff',
  color: '#1d4ed8',
  borderRadius: 5,
  cursor: 'pointer',
  fontSize: 12.5,
  fontWeight: 600,
  padding: '5px 8px',
  whiteSpace: 'nowrap',
}

const trackingListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}

const trackingHeaderStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 48px 46px 58px',
  gap: 9,
  fontSize: 11,
  color: '#6b7280',
  paddingLeft: 2,
}

const trackingRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 48px 46px 58px',
  gap: 9,
  alignItems: 'center',
  minHeight: 34,
  fontSize: 13,
  color: '#374151',
}

const trackingAddButtonStyle: React.CSSProperties = {
  height: 26,
  border: '1px solid #2563eb',
  background: '#ffffff',
  color: '#2563eb',
  borderRadius: 5,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1,
  padding: '0 7px',
  whiteSpace: 'nowrap',
}

const trackingEstimateInputStyle: React.CSSProperties = {
  width: '100%',
  height: 28,
  border: '1px solid transparent',
  borderRadius: 4,
  padding: '0 5px',
  fontSize: 12.5,
  outline: 'none',
  background: 'transparent',
  color: '#374151',
}

const estimateBreakdownListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const estimateBreakdownRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: 12,
  alignItems: 'center',
  minHeight: 28,
  fontSize: 12.5,
  color: '#4b5563',
}

const trackingLoggedStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: '#374151',
  textAlign: 'right',
}

const historyRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '74px minmax(0, 1fr) auto',
  gap: 9,
  alignItems: 'center',
  minHeight: 30,
  borderBottom: '1px solid #eef0f3',
  fontSize: 12.5,
  color: '#4b5563',
}

const historyIconButtonStyle: React.CSSProperties = {
  width: 20,
  height: 20,
  border: 'none',
  background: 'transparent',
  color: '#6b7280',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
}

const historyDeleteButtonStyle: React.CSSProperties = {
  ...historyIconButtonStyle,
  color: '#dc2626',
}

const timeModalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 2,
}

const assigneePlanStyle: React.CSSProperties = {
  marginTop: 14,
  border: '1px solid #eef0f3',
  borderRadius: 6,
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
}

const assigneePlanHeaderStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 48px 52px 44px',
  gap: 8,
  fontSize: 11,
  color: '#8a8f98',
  padding: '0 2px',
}

const assigneePlanRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 48px 52px 44px',
  gap: 8,
  alignItems: 'center',
  fontSize: 12.5,
  color: '#374151',
}

const assigneeBlockStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  borderTop: '1px solid #f1f3f5',
  paddingTop: 7,
}

const assigneeEstimateInputStyle: React.CSSProperties = {
  width: '100%',
  height: 28,
  border: '1px solid #e5e7eb',
  borderRadius: 5,
  padding: '0 6px',
  fontSize: 12.5,
  outline: 'none',
  background: '#ffffff',
}

const assigneeSpendButtonStyle: React.CSSProperties = {
  height: 28,
  border: '1px solid #dfe3ea',
  borderRadius: 5,
  background: '#ffffff',
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: 12.5,
}

const employeeLogListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginLeft: 31,
}

const employeeLogRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  gap: 8,
  alignItems: 'center',
  minHeight: 24,
  fontSize: 12,
  color: '#6b7280',
  background: '#fafafa',
  borderRadius: 5,
  padding: '3px 6px',
}

const stateRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 14,
  alignItems: 'center',
  fontSize: 13,
  color: '#4b5563',
}

const tinyDeleteStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  border: 'none',
  background: '#fee2e2',
  color: '#dc2626',
  borderRadius: 5,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const formLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  marginBottom: 4,
}

const formInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 14,
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  outline: 'none',
  boxSizing: 'border-box',
}
